import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { LookupService } from '@services/lookup.service';
import { StudyService } from '@services/study.service';
import { CompanyNameFilter } from './filter-company-name.model';
import { CompanyNameFilterModel } from '@models/companyNameFilter.model';
import { SelectItem } from 'primeng/api';
import { Country } from '@models/country.model';
import _ from 'lodash-es';
import { Paginator } from 'primeng/paginator';
import { SearchFilterCriteria } from '@models/eliminationMatrixFilterModel';
import { BDMatrixFilter } from '@models/bdMatrixFilter.model';
import * as Constants from '@shared/_constants';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FilterUtilService } from '../shared/filter-util.service';
import { AppState } from '../../../reducers';
import { select, Store } from '@ngrx/store';
import { selectSearchStrategyId } from '../../analysis/search-strategy/state/study-search-strategy.selectors';

@Component({
  selector: 'app-filter-company-name',
  templateUrl: './filter-company-name.component.html',
  styleUrls: ['./filter-company-name.component.css'],
})
export class FilterCompanyNameComponent implements OnInit {
  @ViewChild('footerPaginator', { static: false }) footerPaginator: Paginator;
  @Input() filter;
  @Input() readOnly = false;
  public filterOperationOptions: SelectItem[];

  searchStrategyId: number;
  companyNamePageSize = 10;
  companyNameFilterTotalRecords: number;
  savedCompanies: CompanyNameFilterModel[] = [];
  selectedCompanyCriteria: number;
  submitClicked = false;
  selectedCompanyRows: CompanyNameFilterModel[] = null;
  companySearchCount = 0;
  filterByCompanyValue: string;
  filterCriteria: SearchFilterCriteria = new SearchFilterCriteria();
  companyFilter: BDMatrixFilter = new BDMatrixFilter();
  companyNameFilterResults: CompanyNameFilterModel[];
  filterCountryOptions: Country[];
  validateCriteriaOption = false;
  validateCriteriaValue = false;

  countryId: number;

  private destroy$ = new Subject();

  constructor(
    private lookupService: LookupService,
    private studyService: StudyService,
    private filterUtilService: FilterUtilService,
    private store: Store<AppState>
  ) {}

  ngOnInit() {
    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectSearchStrategyId))
      .subscribe((id) => {
        this.searchStrategyId = id;
      });

    this.loadInitialData();
  }

  async initializeForm(): Promise<void> {
    const filter = <CompanyNameFilter>this.filter;
    if (filter.selectedCompanies && filter.selectedCompanies.length > 0) {
      await this.GetCompanyNameFilterSearchResult(filter, 0, this.companyNamePageSize, true);
      this.savedCompanies = this.savedCompanies.concat(filter.selectedCompanies);
    }
  }

  loadInitialData() {
    this.filterUtilService.spinnerRS.next(true);

    this.selectedCompanyCriteria = undefined;
    this.filterByCompanyValue = undefined;
    this.selectedCompanyRows = [];
    this.companyNameFilterResults = [];
    this.companyNameFilterTotalRecords = 0;
    this.companySearchCount = 0;
    this.filterCountryOptions = [];
    this.filterOperationOptions = null;
    this.selectedCompanyCriteria = undefined;
    this.filterByCompanyValue = undefined;
    this.countryId = undefined;
    this.selectedCompanyRows = [];
    this.companyNameFilterResults = [];
    this.companyNameFilterTotalRecords = 0;
    this.companySearchCount = 0;
    this.filterOperationOptions = this.getFilterOperationOptions();

    this.lookupService
      .GetTestedPartyCountries$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((_filterCountryOptions) => {
        this.filterCountryOptions = _filterCountryOptions;
        this.initializeForm().then(() => {
          this.filterUtilService.spinnerRS.next(false);
        });
      });
  }

  async onCompanyNameSearchClick() {
    if (this.filter instanceof CompanyNameFilter) {
      const criteria = this.filter as CompanyNameFilter;
      if (this.validateCriteria()) {
        this.filterUtilService.spinnerRS.next(true);
        this.validateCriteriaOption = false;
        this.validateCriteriaValue = false;
        this.companyNameFilterResults = null;
        await this.GetCompanyNameFilterSearchResult(criteria, 0, this.companyNamePageSize, true);
        if (this.selectedCompanyRows && this.companyNameFilterTotalRecords > 0) {
          this.companySearchCount = this.companyNameFilterTotalRecords - this.selectedCompanyRows.length;
        } else {
          this.companySearchCount = 0;
        }
        this.footerPaginator.first = 0;
        this.filterUtilService.spinnerRS.next(false);
      }
    }
  }

  private async GetCompanyNameFilterSearchResult(filter: CompanyNameFilter, pageIndex?: number, pageSize?: number, addSelectedCompanies = false) {
    this.filterCriteria.ColumnName = Constants.AddFilterDialogMessages.COMPANY_NAME;
    if (this.selectedCompanyCriteria) {
      this.filterCriteria.FilterCriteria = this.selectedCompanyCriteria;
    }
    if (this.filterByCompanyValue) {
      this.filterCriteria.FilterValue = this.filterByCompanyValue.replace(/'/g, "''").trim(); // replace any single quote as it leads to error in sql query and trim string from both sides
    }
    this.companyFilter.filterParameter = [];
    this.companyFilter.filterParameter.push(this.filterCriteria);
    if (this.countryId) {
      this.companyFilter.countryId = this.countryId;
    }
    this.companyFilter.pageIndex = pageIndex;
    this.companyFilter.pageSize = pageSize;
    this.companyFilter.studySearchStrategyId = this.searchStrategyId;
    if (filter.selectedCompanies && filter.selectedCompanies.length > 0 && addSelectedCompanies === true) {
      this.companyFilter.selectedCompanyIds = filter.selectedCompanies.map((a) => a.comparableId).join(',');
    }

    const result = await this.studyService.GetCompanyNameFilterResultDataSet(this.companyFilter);
    if (result && result.length > 0) {
      this.companyNameFilterResults = result;
      this.companyNameFilterTotalRecords = this.companyNameFilterResults[0][Constants.AddFilterDialogMessages.TOTAL_COUNT];
      this.companyNameFilterResults.forEach((a) => {
        delete a[Constants.AddFilterDialogMessages.TOTAL_COUNT];
      });
      this.selectedCompanyRows = [];
      this.initializeSelected();
    } else {
      this.companyNameFilterResults = null;
      this.companyNameFilterTotalRecords = 0;
    }
  }

  async paginate(event) {
    this.filterUtilService.spinnerRS.next(true);
    if (this.filter instanceof CompanyNameFilter) {
      const criteria = this.filter as CompanyNameFilter;
      await this.GetCompanyNameFilterSearchResult(criteria, event.page, event.rows, false);
    }
    this.filterUtilService.spinnerRS.next(false);
  }

  async resetFilters() {
    this.companySearchCount = 0;
    this.selectedCompanyCriteria = undefined;
    this.filterByCompanyValue = undefined;
    this.countryId = undefined;
    this.validateCriteriaOption = false;
    this.validateCriteriaValue = false;
    this.companyFilter.selectedCompanyIds = undefined;
    const criteria = this.filter as CompanyNameFilter;
    if (this.savedCompanies && this.savedCompanies.length > 0) {
      criteria.selectedCompanies = [];
      criteria.selectedCompanies = criteria.selectedCompanies.concat(this.savedCompanies);
      await this.GetCompanyNameFilterSearchResult(criteria, 0, this.companyNamePageSize, true);
      this.footerPaginator.first = 0;
    } else {
      this.clearSelected();
      this.companyNameFilterResults = [];
      this.companyNameFilterTotalRecords = 0;
    }
  }

  clearSelected(): void {
    const criteria = this.filter as CompanyNameFilter;
    criteria.selectedCompanies = [];
    this.selectedCompanyRows = [];
  }

  validateCriteria(): boolean {
    this.validateCriteriaOption = false;
    this.validateCriteriaValue = false;
    if (this.selectedCompanyCriteria === undefined || this.selectedCompanyCriteria === null) {
      this.validateCriteriaOption = true;
    }
    if (this.filterByCompanyValue === undefined || this.filterByCompanyValue === null || this.filterByCompanyValue === '') {
      this.validateCriteriaValue = true;
    }

    if (this.validateCriteriaOption === true || this.validateCriteriaValue === true) {
      return false;
    }

    return true;
  }

  onRowUnselect(event: any) {
    const criteria = this.filter as CompanyNameFilter;
    criteria.selectedCompanies = criteria.selectedCompanies.filter((s) => s.comparableId !== event.data.comparableId);
  }

  onRowSelect(event: any) {
    const criteria = this.filter as CompanyNameFilter;
    criteria.selectedCompanies.push(event.data);
  }

  onHeaderCheckboxToggle(event, results: any[]): void {
    const criteria = this.filter as CompanyNameFilter;
    if (event.checked) {
      const set = criteria.selectedCompanies.concat(results);
      const uniqueResult: CompanyNameFilterModel[] = [];
      set.forEach((element) => {
        if (uniqueResult && uniqueResult.length > 0) {
          const exist = uniqueResult.find((z) => z.comparableId === element.comparableId);
          if (exist === undefined) {
            uniqueResult.push(element);
          }
        } else {
          uniqueResult.push(element);
        }
      });
      criteria.selectedCompanies = uniqueResult;
    } else {
      criteria.selectedCompanies = _.differenceBy(criteria.selectedCompanies, results, Constants.AddFilterDialogMessages.COMPARABLE_ID);
    }
  }

  private initializeSelected() {
    const criteria = this.filter as CompanyNameFilter;
    const selectedIds = criteria.selectedCompanies.map((c) => c.comparableId);
    this.companyNameFilterResults.forEach((c) => {
      if (selectedIds.indexOf(c.comparableId) > -1) {
        this.selectedCompanyRows.push(c);
      }
    });
  }

  getFilterOperationOptions(): SelectItem[] {
    return [
      { label: Constants.AddFilterDialogMessages.CONTAINS, value: 0 },
      { label: Constants.AddFilterDialogMessages.BEGINS_WITH, value: 1 },
    ];
  }
}
